
import { marked } from "marked";

import {
  ApiListItem,
  CalcInfo,
  CalcRowsLogic,
  DatelyObject,
  DateRange,
  Dates,
  DateTimes,
  EmploymentData,
  EnumerationsLogic,
  Iban,
  Numeric,
  Occupations,
  Role,
  SitemapNode,
  SystemRole,
  Translations,
  Unit,
  WorkflowEvent
} from "@salaxy/core";

import { NaviService, SessionService } from "../services";
import { SitemapHelper } from "../helpers";

/**
 * Contains the simple filter functions where implementation
 * is in some other class (typically @salaxy/core) and thus the filter implementation is very small.
 * This should typically the case, but some filters may contain a lot of NG1 specific
 * implementation (e.g. SalaxyTranslateFilter) and/or dependency injection and thus deserve their own class.
 */
export class FilterFunctions {

  /**
   * Formats a Workflow message string from index.
   * List of allowed message types can be given as a comma seprated list.
   *
   * Parameters of the filter are:
   *
   * - messageString: Workflow message
   * - types: Allowed message types.
   */
  public static sxyWorkflowMessage = ["$sce", ($sce: angular.ISCEService) => {
    return (messageString: string, types: string) => {
      if (!messageString) {
        return $sce.getTrustedHtml("<span></span>");
      }
      const messageTypes = (types || "").split(",");
      const uiClassMarker = messageString.indexOf("(Ui");
      const uiClassEndMarker = messageString.indexOf("):", uiClassMarker + 3);
      const type = (messageString.substring(0, uiClassMarker - 1) || "UnknownEvent");

      if (messageTypes.length > 0) {
        if (!messageTypes.some((x) => x.trim() === type)) {
          return $sce.getTrustedHtml("<span></span>");
        }
      }

      if (type.startsWith("SystemMessage")) {
        const wfEvent = FilterFunctions.toWorkflowEvent(messageString);
        const time = wfEvent.time ? Dates.format(wfEvent.time, "D.M.YYYY HH:mm") : "";
        let shortMessage = type.substring(13);
        shortMessage = shortMessage.substring(0, 1).toLowerCase() + shortMessage.substring(1);
        shortMessage = Translations.get("SALAXY.ENUM.SystemMessage." + shortMessage + ".label" ) as string;
        const fullMessage = (wfEvent.message + " " + time).trim();
        return $sce.getTrustedHtml(`<span class="badge bg-${wfEvent.ui}" title="${fullMessage}">${shortMessage}</span>`);
      }
      else {

        const uiClass = messageString.substring(uiClassMarker + 3, uiClassEndMarker).toLowerCase();
        let fullMessage = messageString.substring(uiClassEndMarker + 3);
        const dtReg = /([0-9]{4})-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1]) (2[0-3]|[01][0-9]):([0-5][0-9])/g;
        fullMessage = fullMessage.replace(dtReg, (match: string, year, month, day, hours, minutes) => {
          const d = Date.UTC(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10), parseInt(hours, 10), parseInt(minutes, 10));
          return Dates.format(d, "D.M.YYYY HH:mm");
        });
        const shortMessage = fullMessage.substring(0, fullMessage.lastIndexOf("("));
        return $sce.getTrustedHtml(`<span class="badge bg-${uiClass}" title="${fullMessage}">${shortMessage}</span>`);
      }
    };
  }];

  private static toWorkflowEvent(messageString: string): WorkflowEvent {
    if (!messageString) {
      return {};
    }
    // [type] (Ui[ui]):[message] ([User] at [yyyy-MM-dd HH:mm] to [assignedTo] id [id])
    const uiStartX = messageString.indexOf("(Ui");
    const uiEndX = messageString.indexOf("):", uiStartX + 3);
    const type = (messageString.substring(0, uiStartX - 1) || "");
    const ui = messageString.substring(uiStartX + 3, uiEndX).toLowerCase();
    let rest = messageString.substring(uiEndX + 3);
    const messageEndX = rest.lastIndexOf("(");
    const message = rest.substring(0, messageEndX).trim();
    rest = rest.substring(messageEndX + 1); // Remove (
    rest = rest.substring(0, rest.length - 1); // Remove )
    let id = "";
    const idx = rest.lastIndexOf(" id ");
    if (idx >= 0) {
      id = rest.substring(idx + 4);
      rest = rest.substring(0, idx);
    }
    let assignedTo = "";
    const aIdx = rest.lastIndexOf(" to ");
    if (aIdx >= 0) {
      assignedTo = rest.substring(aIdx + 4);
      rest = rest.substring(0, aIdx);
    }
    let time = "";
    const tIdx = rest.lastIndexOf(" at ");
    if (tIdx >= 0) {
      time = rest.substring(tIdx + 4);
      rest = rest.substring(0, tIdx);
      // time
      time = time.replace(" ", "T") + "Z";
    }
    const user = rest;
    return {
      id,
      type,
      ui,
      user,
      assignedTo,
      message,
      time,
    } as WorkflowEvent;
  }

  public static sxyShow = ["NaviService", (naviService: NaviService) => {
    return (sectionToCheck: string, sitemapPath: string): boolean => {
      if (!sectionToCheck || !sitemapPath) {
        return true;
      }
      const splitPaths = sitemapPath.split(".");
      let sitemap: SitemapNode[] = naviService.getSitemap();
      const companySite = sitemap.find(node => node.id === "company-site");
      if (companySite) {
        sitemap = [companySite];
      }
      let resultSitemapNode: SitemapNode;
      if (splitPaths.length >= 3) {
        let currentSection: SitemapNode[] = sitemap;
        for (const path of splitPaths) {
          const section = SitemapHelper.getSection(currentSection, path);
          if (path === splitPaths[splitPaths.length - 1]) {
            resultSitemapNode = section;
          } else {
            currentSection = section.children;
          }
        }
      } else if (splitPaths.length === 2) {
        resultSitemapNode = SitemapHelper.getNode(sitemap, splitPaths[0], splitPaths[1]);
      } else {
        resultSitemapNode = SitemapHelper.getSection(sitemap, splitPaths[0]);
      }
      if (resultSitemapNode) {
        return !(resultSitemapNode?.hidden ?? []).includes(sectionToCheck);
      }
      return true;
    };
  }];

  /**
   * Translate filter with key and interpolation parameter.
   * Converts content with translationId ending with '.html', to safe html.
   * Converts content with translationId ending with '.md', first to html and then to safe html.
   * If the text contains a variable placeholder like: 'Hello {{name}}!', it will be replaced by given parameter.
   *
   * Parameters of the filter are:
   *
   * - translationId: Translation key
   * - interpolateParams: Interpolation parameters applied if text had interpolation.
   * @example
   * ```html
   * <p sxy-text="SALAXY.NG1.test1"></p>
   * <p>{{ 'SALAXY.NG1.hello' | sxyTranslate: { name: 'John'} }}</p>
   * ```
   */
  public static sxyTranslate = ["$sce", ($sce: angular.ISCEService) => {
    return (translationId: string, interpolateParams: any): string => {
      const endsWith = (str1: string, str2: string) => {
        return str1.substring(str1.length - str2.length, str1.length) === str2;
      };
      const str = Translations.get(translationId, interpolateParams) as string;
      if (translationId) {
        if (endsWith(translationId, ".html")) {
          return $sce.getTrustedHtml(str);
        } else if (endsWith(translationId, ".md")) {
          return $sce.getTrustedHtml(marked(str));
        }
      }
      return str;
    };
  }];

  /**
   * Formats a string using markdown and returns trusted HTML.
   * Parameters of the filter are:
   * - markdown: Markdown input to format as HTML.
   * @example
   * ```html
   * <div ng-bind-html="$ctrl.current.example | sxyMarkdown"></div>
   * ```
   */
  public static sxyMarkdown = ["$sce", ($sce: angular.ISCEService) => {
    return (markdown: string): string => {
      if (markdown == null) {
        return null;
      }
      return $sce.getTrustedHtml(marked(markdown));
    };
  }];

  /**
   * IsInRole filter returns true if the user is in any of the given roles.
   * The input may be Array of roles or comma separated string containing role names.
   * This can be used instead of sxy-if-role directive when the component requires transcusion
   * and also in constructing other logic in expressions.
   *
   * Parameters of the filter are:
   *
   * - commaSeparatedRolesList: Comma separated list of roles
   *
   * NOTE: Because the actual value is constant, the filter is not rerun after session check).
   * Because of this, the method should only be used after the session has been checked.
   * Typically, by making sure, the filters are inside `sxy-if-role="$ctrl.current"`, `sxy-if-role="auth"` or `sxy-if-role="!unknown"`.
   * @example
   * ```html
   * <div sxy-if-role="auth">
   *   <uib-tab index="'holidays'" heading="Lomat" ng-if="'company' | sxyIsInRole">
   *   <p>{{ 'company' | sxyIsInRole ? 'Company text' : 'Default text' }}</p>
   * </div>
   * ```
   */
  public static sxyIsInRole = ["SessionService", (sessionService: SessionService) => {
    return (commaSeparatedRolesList: (SystemRole | Role | string)[] | string): boolean => {
      return sessionService.isInSomeRole(commaSeparatedRolesList);
    };
  }];

  /**
   * IsVisibility filter returns true/false when evaluating the logical visibility of a sitemap,
   * as used in the views an UI logic, i.e.:
   *
   * - hidden: Node is null or visibility is Hidden, HiddenReadOnly or Disabled.
   * - readOnly: Node is null or visibility is ReadOnly, HiddenReadOnly or Disabled.
   * - disabled: Visibility is Disabled.
   * - Visible: Visibility is Visible or null, node is not null.
   * - isFullWidth: The node has an isFullWidth boolean flag set to true.
   *
   * This can be used instead of sxy-if-sitemap directive when the component requires transcusion
   * and also in constructing other logic in expressions.
   *
   * Parameters of the filter are:
   *
   * - visibilityToEvaluate: The visibility value to evaluate for.
   *
   * NOTE: Because the actual value is constant, the filter is not rerun after session check).
   * Because of this, the method should only be used after the session has been checked.
   * Typically, by making sure, the filters are inside `sxy-if-role="$ctrl.current"`, `sxy-if-role="auth"` or `sxy-if-role="!unknown"`.
   * @example
   * ```html
   * <div sxy-if-role="auth">
   *   <uib-tab index="'holidaysReadOnly'" heading="Lomat (Read-only)" ng-if="'readOnly' | sxyIsVisibility">
   *   <p>{{ 'readOnly' | sxyIsVisibility ? 'Read-only text' : 'Default text' }}</p>
   * </div>
   * ```
   */
  public static sxyIsVisibility = ["NaviService", (naviService: NaviService) => {
    return (visibilityToEvaluate: string): boolean => {
      // TODO: For some reason Rollup fails in build if type is set to SitemapVisibility (from core).
      return naviService.current.getVisibility(visibilityToEvaluate as any);
    };
  }];

  /**
   * Returns the label for an enumeration.
   *
   * Parameters of the filter are:
   *
   * - input: Enumeration in format "[EnumType].[EnumName]". First letter is case-insensitive so you can use lowerCamelCase or UpperCamelCase.
   * - defaultValue: Optional default value when language version is not available.
   * Special values "name" and "#name", may be used to return the enum member name either with hash or without it.
   * Default is "#name", which returns "#[MemberName]" if the translation is not found.
   * @example
   * ```html
   * <p>{{ 'CalculationStatus.Draft' | sxyEnum }} - The enum full name</p>
   * <p>{{ 'CalculationStatus.paymentSucceeded' | sxyEnum }} - you may also use JSON value where first letter is lower-case.</p>
   * <p>{{ 'CalculationStatus.' + calc.status | sxyEnum }} - ...which makes it easy to use with data.</p>
   * ```
   */
  public static sxyEnum() {
    return (input: string, defaultValue: null | string | "name" | "#name" = "#name") => {
      const splitValues = (input || "").split(".");
      if (splitValues.length !== 2) {
        return "#ERR: sxyEnum";
      }
      if (!splitValues[1]) {
        return null;
      }
      return EnumerationsLogic.getEnumLabel(splitValues[0], splitValues[1], defaultValue);
    };
  }

  /**
   * Returns the description for an enumeration value in current language.
   *
   * Parameters of the filter are:
   *
   * - input: Enumeration in format "[EnumType].[EnumName]". First letter is case-insensitive so you can use lowerCamelCase or UpperCamelCase.
   * - defaultValue: Optional default value when language version is not available. Default is null
   * Special values "name" and "#name", may be used to return the enum member name either with hash or without it.
   * @example
   * ```html
   * <p>{{ 'CalculationStatus.Draft' | sxyEnumDescr }} - The enum full name</p>
   * <p>{{ 'CalculationStatus.paymentSucceeded' | sxyEnumDescr }} - you may also use JSON value where first letter is lower-case.</p>
   * <p>{{ 'CalculationStatus.' + calc.status | sxyEnumDescr }} - ...which makes it easy to use with data.</p>
   * ```
   */
  public static sxyEnumDescr() {
    return (input: string, defaultValue?: null | string | "name" | "#name") => {
      const splitValues = (input || "").split(".");
      if (splitValues.length !== 2) {
        return "#ERR: sxyEnumDescr";
      }
      if (!splitValues[1]) {
        return null;
      }
      return EnumerationsLogic.getEnumDescr(splitValues[0], splitValues[1], defaultValue);
    };
  }

  /**
   * Formats the date with Dates.getFormattedDate in core project.
   * If the input is not recognized as date or if it is empty or less than year 1900, shows a dash.
   *
   * Parameters of the filter are:
   *
   * - input: Input to format
   * @example
   * ```html
   * <p>{{ calc.workflow.paidAt | sxyDate }}</p>
   * ```
   */
  public static sxyDate() {
    return (input: string) => {
      const year = Dates.getYear(input);
      if (!year || year < 1900) {
        return "-";
      }
      return Dates.getFormattedDate(input);
    };
  }

  /**
   * Formats a DateRange with Dates.getFormattedRange() in core project.
   * If input is null, shows a dash.
   * Also supports the ApiListItem (startAt, endAt), CalcInfo type in Calculation.info property (workStartDate and workEndDate) and EmploymentData (startDate and endDate)
   *
   * Parameters of the filter are:
   *
   * - input: Input to format
   *
   */
  public static sxyDateRange() {
    return (input: DateRange) => {
      if (!input) {
        return "-";
      }
      const info = input as CalcInfo;
      if (info.workStartDate || info.workEndDate) {
        return Dates.getFormattedRange(info.workStartDate, info.workEndDate);
      }
      const listItem = input as ApiListItem;
      if (listItem.startAt || listItem.endAt) {
        return Dates.getFormattedRange(listItem.startAt, listItem.endAt);
      }
      const employmentData = input as EmploymentData;
      if (employmentData.startDate || employmentData.endDate) {
        return Dates.getFormattedRange(employmentData.startDate, employmentData.endDate);
      }
      return Dates.getFormattedRange(input.start, input.end);
    };
  }

  /**
   * Iban formatting filter.
   *
   * Parameters of the filter are:
   *
   * - input: Input text to format as IBAN.
   *
   */
  public static sxyIban() {
    return (input: string) => {
      if (!input) {
        return null;
      }
      return Iban.formatIban(input);
    };
  }

  /**
   * Formats a number as count optionally taking into account the related unit
   *
   * Parameters of the filter are:
   *
   * - input: Input to format
   * - unit: Unit that is used in formatting.
   * - decimals: Number of decimals to show.
   * - nullText: Text to show if the value is null / undefined. By default returns null, which can then be cought by another filter or logical or ("||").
   * - zeroText: If specified, will use this text without the Unit formatting if the value is zero. By default zero is formatted as "0 [unitText]".
   */
  public static sxyCount() {
    return (
      input: number,
      unit: Unit = null,
      decimals: number = null,
      nullText: string = null,
      zeroText: string = null) => {
      if (input == null) {
        return nullText;
      }
      if (zeroText != null && input === 0) {
        return zeroText;
      }
      const rounded = Numeric.round(input, decimals || 2);
      switch (unit) {
        case Unit.One:
          return "";
        case Unit.Period:
          return rounded + Translations.get("SALAXY.NG1.FilterFunctions.sxyCount.period" + (rounded === 1 ? "" : "s"));
        case Unit.Percent:
          return Numeric.round(input * 100, decimals || 2) + "%";
        case Unit.Hours:
        case Unit.Days:
        case Unit.Weeks:
        case Unit.Count:
        case Unit.Kilometers:
        case Unit.Undefined:
        default:
          return rounded + " " + (CalcRowsLogic.getUnitIndicator(unit) || "");
      }
    };
  }

  /**
   * Formats a datetime ISO string (or other DayJS-object compatible time)
   * As time with texts "Today", "Yesterday", "D.M. HH:mm" for this year
   * and "D.M.YYYY" for other years (if necessary, add option for adding time to previous years).
   *
   * Parameters of the filter are:
   *
   * - time: Input to format
   *
   */
  public static sxyTime() {
    // TODO: This is being rewritten in CalendarEventsCrudController. When ready, move to core and use the same function here.
    return (time: string) => {
      return DateTimes.format(time);
    };
  }

  /**
   * Calls the Salaxy standard Dates.asDate to convert a DatelyObject to ISO date string (without time).
   * ISO dates can then be used in comparison and equality.
   * Also note that, DatelyObject can be constants like "today" | "yesterday" | "tomorrow". See documentation for details.
   *
   * Parameters of the filter are:
   *
   * - input: Input to format as DatelyObject
   * - addWorkDays: If set, adds the number of workdays to the input.
   * May also be zero (0) to return input if it is a workday or next workday if input is not a workday.
   *
   */
  public static sxyAsDate() {
    return (input: DatelyObject, addWorkDays: number) => {
      if (addWorkDays != null) {
        return Dates.addWorkdays(input, addWorkDays);
      }
      return Dates.asDate(input);
    };
  }
  /**
   * Formats a date/time range to an end-user friendly duration text.
   * Input may be Duration as ISO duration string (e.g. P3Y6M4DT12H30M5S) or .Net timespan (e.g. '7.23:59:59.999').
   */
  public static sxyDuration() {
    return (input: string) => {
      return DateTimes.formatDuration(input);
    };
  }

  /**
   * Converts the occupation to a language versioned string.
   *
   * Parameters of the filter are:
   *
   * - input: The occupation code as stored / returned from the API.
   * - format: Default is just the label for the occupation. "code-label" is in format "[code]: [label]`
   *
   */
  public static sxyOccupation() {
    return (input: string, format: "default" | "code-label") => {
      const occupation = Occupations.getById(input);
      if (!occupation) {
        return Translations.get("SALAXY.NG1.FilterFunctions.sxyOccupation.occupationMissing");
      }
      if (format === "code-label") {
        return `${occupation.code}: ${occupation.label}`;
      }
      return occupation.label;
    };
  }

  /**
   * Formats a time (input) to an end-user friendly totaltime (12:34 or 12:34:56) text.
   * Input may be Duration as ISO duration string (e.g. P3Y6M4DT12H30M5S) or .Net timespan (e.g. '7.23:59:59.999').
   */
  public static sxyTotalTime() {
    return (input: number, format: "minutes" | "seconds") => {
      const padTo2Digits = (num) => {
        return num.toString().padStart(2, "0");
      };
      input = Numeric.isNumber(input) ? input : 0;
      switch (format) {
        case "minutes": {
          const hours = Math.floor(input / 60);
          const minutes = input % 60;
          return `${padTo2Digits(hours)}:${padTo2Digits(minutes)}`;
        }
        case "seconds": {
          const hours = Math.floor(input / 3600);
          const minutes = Math.floor((input % 3600) / 60);
          const seconds = input % 60;
          return `${padTo2Digits(hours)}:${padTo2Digits(minutes)}:${padTo2Digits(seconds)}`;
        }
      }
    };
  }

}
